import React from "react";
import { FileText, FileSpreadsheet, Image as ImageIcon, Mail, File, PenTool, Archive } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ITEM_STATE, REASON_BADGE, PRICE_TYPE_LABEL, PRICE_TYPE_SHORT, DOC_STATUS, DOC_ROLE, sourceHref } from "@/lib/sk";

export const pill = "inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium transition-colors disabled:opacity-40";
export const pillGhost = cn(pill, "text-foreground/70 hover:bg-black/[0.05] hover:text-foreground");
export const pillSolid = cn(pill, "bg-[hsl(var(--brand))] text-white hover:bg-[hsl(var(--brand-strong))]");
export const pillLine = cn(pill, "border border-black/[0.09] bg-white hover:border-black/[0.2]");
export const inputCls = "h-8 rounded-md border border-black/[0.1] bg-white px-2 text-[13px] outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/15";

/** Pass `calc` to get the precise, calm status (reason-based); without it the coarse state label is shown.
 *  Quiet states render as plain muted text – a badge is only drawn when a decision is needed. */
export function StateBadge({ state, calc, className, testId }) {
  const r = calc?.reason_code ? REASON_BADGE[calc.reason_code] : null;
  const s = r || ITEM_STATE[state] || ITEM_STATE.ma_vurderes;
  const label = typeof s.label === "function" ? s.label(calc?.open_clarifications || 0) : s.label;
  if (s.quiet) return <span className={cn("inline-flex h-[22px] items-center whitespace-nowrap text-[12.5px] text-muted-foreground", state === "utgar" && "line-through", className)} data-testid={testId} data-state={state} data-reason={calc?.reason_code || undefined} data-quiet="1" title={calc?.reason || undefined}>{label}</span>;
  return <span className={cn("v-badge", s.cls, className)} data-testid={testId} data-state={state} data-reason={calc?.reason_code || undefined} title={calc?.reason || undefined}>{label}</span>;
}

export function PriceTypeTag({ priceType, className }) {
  if (!priceType) return <span className={cn("text-[13px] text-muted-foreground/60", className)}>Velg pristype</span>;
  return <span className={cn("text-[13px] text-foreground/80", className)} title={PRICE_TYPE_LABEL[priceType] || priceType}>{PRICE_TYPE_SHORT[priceType] || priceType}</span>;
}

export function DocStatusBadge({ status, note, className, testId }) {
  const s = DOC_STATUS[status] || DOC_STATUS.mottatt;
  const badge = <span className={cn("v-badge", s.cls, className)} data-testid={testId}>{s.label}</span>;
  if (!note) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px] rounded-lg border-black/[0.08] bg-white px-2.5 py-1.5 text-[11.5px] text-foreground shadow-float">{note}</TooltipContent>
    </Tooltip>
  );
}

export function RoleTag({ role, className }) {
  const r = DOC_ROLE[role] || DOC_ROLE.ukjent;
  return <span className={cn("inline-flex items-center gap-1 text-[11.5px] text-muted-foreground", className)}><span className="font-mono text-[10.5px] text-muted-foreground/70">{r.n}</span>{r.label}</span>;
}

const KIND_ICON = { pdf: FileText, xlsx: FileSpreadsheet, docx: FileText, image: ImageIcon, msg: Mail, dwg: PenTool, zip: Archive, text: FileText };

export function DocIcon({ kind, className }) {
  const Icon = KIND_ICON[kind] || File;
  return <Icon className={cn("h-3.5 w-3.5 shrink-0 text-primary", className)} strokeWidth={1.9} />;
}

/** Where a value came from: PDF page or sheet row/cell. A real link to the in-app source viewer (opens at that page / cell). */
export function SourceChip({ source, sheetRef, onOpen, caseId, className, testId, queries }) {
  const chips = [];
  const pdfKind = /spes|spec|foresp|yard|job/i.test(source?.doc_name || "") ? "Spes." : "PDF";
  if (source?.page) chips.push({ key: "pdf", label: `${pdfKind} s. ${source.page}`, title: `${source.doc_name} · side ${source.page}${source.pages?.length > 1 ? `–${source.pages[source.pages.length - 1]}` : ""} · åpner på siden`, docId: source.doc_id, where: { page: source.page, pages: source.pages, doc_name: source.doc_name, q: queries?.filter(Boolean) }, Icon: FileText });
  if (sheetRef?.row) chips.push({ key: "sheet", label: `Svarark ${sheetRef.cells?.price ? sheetRef.cells.price : `rad ${sheetRef.row}`}`, title: `${sheetRef.doc_name} · ark «${sheetRef.sheet}» · rad ${sheetRef.row}${sheetRef.cells?.price ? ` · priscelle ${sheetRef.cells.price}` : ""} · åpner cellen`, docId: sheetRef.doc_id, where: { sheet: sheetRef.sheet, row: sheetRef.row, cell: sheetRef.cells?.price, doc_name: sheetRef.doc_name }, Icon: FileSpreadsheet });
  if (source?.type === "manuelt") chips.push({ key: "man", label: "manuelt", title: `Lagt inn manuelt${source.by ? ` av ${source.by}` : ""}`, docId: null, Icon: PenTool });
  if (!chips.length) return null;
  return (
    <span className={cn("inline-flex flex-wrap gap-1", className)} data-testid={testId}>
      {chips.map((c) => {
        const openable = Boolean(c.docId && onOpen);
        const cls = cn("src-ref", openable ? "cursor-pointer hover:border-black/[0.2] hover:text-foreground" : "cursor-default");
        const tid = testId ? `${testId}-${c.key}` : undefined;
        const inner = <><c.Icon className="h-2.5 w-2.5" />{c.label}</>;
        return (
          <Tooltip key={c.key}>
            <TooltipTrigger asChild>
              {openable && caseId
                ? <a href={sourceHref(caseId, c.docId, c.where)} target="_blank" rel="noreferrer" onClick={(e) => { e.stopPropagation(); if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) { e.preventDefault(); onOpen(c.docId, c.where); } }} className={cls} data-testid={tid} data-doc-id={c.docId}>{inner}</a>
                : <button type="button" onClick={(e) => { e.stopPropagation(); if (openable) onOpen(c.docId, c.where); }} className={cls} data-testid={tid}>{inner}</button>}
            </TooltipTrigger>
            <TooltipContent side="top" className="rounded-lg border-black/[0.08] bg-white px-2.5 py-1.5 font-mono text-[11px] text-foreground shadow-float">{c.title}</TooltipContent>
          </Tooltip>
        );
      })}
    </span>
  );
}

export function SectionLabel({ children, right, className }) {
  return (
    <div className={cn("flex items-center justify-between gap-3", className)}>
      <h3 className="text-[13.5px] font-medium text-foreground/80">{children}</h3>
      {right}
    </div>
  );
}

export function Empty({ children, className, testId }) {
  return <p className={cn("text-[12.5px] text-muted-foreground/80", className)} data-testid={testId}>{children}</p>;
}
